import React, { useEffect, useRef, useState } from "react";
import { getStompClient } from "../../../StompClient";

interface TerminalProps {
    activityId: string | undefined;
    userId: string | undefined;
    setIsRunning?: (running: boolean) => void;
    setIsLoading?: (loading: boolean) => void;
    onMessage?: (msg: string) => void;
}

const TerminalJava: React.FC<TerminalProps> = ({ activityId, userId, setIsRunning, setIsLoading, onMessage }) => {
    const [lines, setLines] = useState<string[]>([]);
    const [input, setInput] = useState("");
    const terminalRef = useRef<HTMLDivElement>(null);

    // Scroll automático
    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [lines]);

    useEffect(() => {
        if (!activityId || !userId) return;

        const client = getStompClient();
        let subscription: any;

        const subscribe = () => {
            if (subscription) return;

            subscription = client.subscribe(
                `/topic/compilador/${activityId}/${userId}`,
                (msg: any) => {
                    let data: any;
                    try {
                        data = JSON.parse(msg.body);
                    } catch {
                        data = { contenido: msg.body, tipo: "OUTPUT" };
                    }

                    const prefix = data.tipo === "ERROR" ? "[ERROR] " : "";
                    const fullMsg = prefix + data.contenido;
                    setLines(prev => [...prev, fullMsg]);
                    if (onMessage) onMessage(fullMsg);

                    // Informar al ExamenModal si la compilación terminó
                    if (data.tipo === "END" || data.tipo === "ERROR_END") {
                        window.dispatchEvent(new Event("compilationEnded")); // mantiene compatibilidad
                        if (setIsRunning) setIsRunning(false);
                        if (setIsLoading) setIsLoading(false);
                    }
                }
            );
        };

        if (client.connected) {
            subscribe();
        } else {
            const originalOnConnect = client.onConnect;
            client.onConnect = (frame) => {
                if (originalOnConnect) originalOnConnect(frame);
                subscribe();
            };
        }

        return () => {
            if (subscription) subscription.unsubscribe();
            subscription = null;
        };
    }, [activityId, userId, setIsRunning, setIsLoading]);

    useEffect(() => {
        const handleClear = () => {
            setLines([]);
            if (onMessage) onMessage("__CLEAR__");
        };
        window.addEventListener("clearTerminal", handleClear);
        return () => window.removeEventListener("clearTerminal", handleClear);
    }, [onMessage]);

    const sendInput = () => {
        if (!input.trim() || !activityId || !userId) return;

        const client = getStompClient();
        if (!client.connected) return;

        client.publish({
            destination: `/app/input/${activityId}/usuario/${userId}`,
            body: input,
        });

        setLines(prev => [...prev, "> " + input]);
        setInput("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") sendInput();
    };

    return (
        <div
            style={{
                background: "#1e1e1e",
                color: "white",
                fontFamily: "monospace",
                padding: 10,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 4,
            }}
        >
            <div
                ref={terminalRef}
                style={{ flex: 1, overflowY: "auto", whiteSpace: "pre-wrap" }}
            >
                {lines.map((line, i) => (
                    <div key={i}>{line}</div>
                ))}
            </div>

            <div style={{ display: "flex", marginTop: 5 }}>
                <span style={{ marginRight: 5 }}>{">"}</span>
                <input
                    style={{
                        flex: 1,
                        background: "transparent",
                        color: "white",
                        border: "none",
                        outline: "none",
                        fontFamily: "monospace",
                    }}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                />
            </div>
        </div>
    );
};

export default TerminalJava;
